import { useEffect, useState } from "react";
import { FileText, Download, ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import api from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;
const fmtN = (n) => (n == null ? "—" : Number(n).toLocaleString("fr-FR"));

function KpiGrid({ social }) {
  const t = social?.totals || {};
  const rows = [
    ["Abonnés", t.followers], ["Portée", t.reach], ["Impressions", t.impressions],
    ["Interactions", t.interactions], ["Clics lien", t.clicks], ["Conversions", t.conversions],
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="client-report-kpis">
      {rows.map(([l, v]) => (
        <div key={l} className="bg-neutral-50 border border-neutral-100 rounded-lg p-3">
          <p className="font-heading text-lg font-bold text-neutral-900">{fmtN(v)}</p>
          <p className="text-[11px] text-neutral-500">{l}</p>
        </div>
      ))}
    </div>
  );
}

export default function CMClientReports() {
  const [reports, setReports] = useState(null);
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get("/graphisme/espace/cm/reports").then((r) => setReports(r.data || [])).catch(() => setReports([]));
  }, []);

  const toggle = async (id) => {
    if (open === id) { setOpen(null); return; }
    setOpen(id);
    setDetail(null);
    try {
      const { data } = await api.get(`/graphisme/espace/cm/reports/${id}`);
      setDetail(data);
    } catch { setDetail(null); }
  };

  if (reports === null) {
    return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-neutral-300" /></div>;
  }

  return (
    <div className="space-y-3" data-testid="cm-client-reports">
      {reports.length === 0 && (
        <p className="text-sm text-neutral-400 bg-white border border-dashed border-neutral-300 rounded-xl p-6 text-center">
          Aucun rapport publié pour le moment — votre agence vous notifiera dès qu'un rapport sera disponible.
        </p>
      )}
      {reports.map((r) => (
        <div key={r.id} className="bg-white border border-neutral-200 rounded-xl overflow-hidden" data-testid={`cm-client-report-${r.id}`}>
          <div className="flex items-center gap-3 px-4 py-3">
            <span className="w-9 h-9 rounded-lg bg-neutral-900 flex items-center justify-center shrink-0">
              <FileText className="w-4 h-4 text-white" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-neutral-900 capitalize truncate">
                Rapport Social Media — {r.period_label}
              </p>
              <p className="text-[11px] text-neutral-400">
                Rapport Community Management
                {r.published_at && ` · publié le ${new Date(r.published_at).toLocaleDateString("fr-FR")}`}
              </p>
            </div>
            <button onClick={() => toggle(r.id)} data-testid={`cm-client-report-open-${r.id}`}
              className="inline-flex items-center gap-1 text-xs font-semibold text-neutral-700 hover:text-neutral-900 px-2 py-1.5">
              {open === r.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />} Consulter
            </button>
            <a href={`${API}/api/graphisme/espace/cm/reports/${r.id}/pdf`} data-testid={`cm-client-report-pdf-${r.id}`}
              className="text-xs bg-neutral-900 text-white rounded-lg px-3 py-1.5 hover:bg-neutral-800 inline-flex items-center gap-1 shrink-0">
              <Download className="w-3.5 h-3.5" /> PDF
            </a>
          </div>
          {open === r.id && (
            <div className="border-t border-neutral-100 px-5 py-4 space-y-4" data-testid={`cm-client-report-detail-${r.id}`}>
              {!detail && <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-neutral-300" /></div>}
              {detail && (
                <>
                  {detail.note_agence && (
                    <p className="text-[13px] text-neutral-600 italic bg-amber-50 border-l-2 border-amber-400 rounded-r-lg px-3 py-2.5">
                      {detail.note_agence}
                    </p>
                  )}
                  <KpiGrid social={detail.stats?.social} />
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400 mb-1.5">Analyse de votre agence</p>
                    <p className="text-[13px] text-neutral-700 whitespace-pre-line leading-relaxed">{detail.insights}</p>
                  </div>
                  {(detail.stats?.social?.top_publications || []).length > 0 && (
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400 mb-1.5">Publications les plus performantes</p>
                      {detail.stats.social.top_publications.map((t, i) => (
                        <div key={i} className="flex items-center gap-3 py-1.5 text-[12px] border-b border-neutral-50 last:border-0">
                          <span className="flex-1 min-w-0 truncate font-medium text-neutral-800">{t.title}</span>
                          <span className="text-neutral-400 shrink-0">{t.format}</span>
                          <span className="text-neutral-500 shrink-0">Portée {fmtN(t.reach)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
